import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getSampleCsvFile, uploadCsvFile } from '~/api/automation';
import { Box } from '~/eds/src/components/Box/Box';
import { CollapsibleText } from '~/eds/src/components/CollapsibleText/CollapsibleText';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Link } from '~/eds/src/components/Link/Link';
import { Section } from '~/eds/src/components/Section/Section';
import { Stepper } from '~/eds/src/components/Stepper/Stepper';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { QueryParamType } from '~/enums/QueryParamType';
import { TrainingStage } from '~/enums/TrainingStage';
import { useIntl } from 'react-intl';
import { slices } from '~/redux/slices';
import Alert from '~/ui/components/Alert/Alert';
import FileInput from '~/ui/components/FileInput/FileInput';
import FileLabel from '~/ui/components/FileLabel/FileLabel';
import { useModal } from '~/ui/hooks/modal';
import { exportCsvBlob } from '~/utils/helper.utils';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';

import AutomationHubSearchingFilters from '../../AutomationHubSearchFilters/AutomationHubSearchingFilters';
import {
  CSV_TEMPLATE_HEADER,
  SAMPLE_FILE_ERRORS,
  SIDE_BAR_WIDTH,
} from '../../constants';

const GuidedAiForm = () => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const TRAINING_FILTER_TEXT = intl.formatMessage({
    id: 'GuidedAiForm.trainingFilterText',
    defaultMessage: `You may set up filters to allow the model to focus its learning on specific contracts. That can help achieve the highest accuracy when identifying the Clause in the future. For example, suppose you have a Clause that is specific to contracts stored in a particular folder. You should set training filters to target that folder so that the model's training is most effective. If you don't limit training, then the model may learn from contracts that aren't applicable to the Clause. The end result would be a model that may misidentify contracts in the future. Don't fret if you don't have a way to limit your contracts. It's possible that your Clause is generic enough that it does need to be identified across all your contracts. In this case, it makes sense that you don't want to apply filters.`,
    description: 'Explanation text for training filters section',
  });
  const clause = useSelector(
    slices.automation.selectors.selectAutomationClause,
  );
  const guidedAiForm = useSelector(
    slices.automation.selectors.selectAutomationGuidedAiForm,
  );
  const filters = useSelector(
    slices.automation.selectors.selectAutomationFilters,
  );
  const model = useSelector(slices.automation.selectors.selectAutomationModel);

  const shouldShowTemplate = SAMPLE_FILE_ERRORS.includes(
    guidedAiForm.invalids?.error,
  );

  const trainingDataError = (
    <Layout direction="column" align="center" spacing={2}>
      <Text color="status.danger">{guidedAiForm.invalids?.error}</Text>
      {shouldShowTemplate && (
        <Text color="status.danger">
          {intl.formatMessage({
            id: 'GuidedAiForm.csvTemplateError',
            defaultMessage: 'Make sure that your file conforms to the ',
            description: 'Prompt to use CSV template when file error occurs',
          })}
          <Link
            onClick={() => exportCsvBlob(CSV_TEMPLATE_HEADER, 'template.csv')}
          >
            {intl.formatMessage({
              id: 'GuidedAiForm.csvTemplateLink',
              defaultMessage: 'CSV template.',
              description: 'CSV template link text',
            })}
          </Link>
        </Text>
      )}
    </Layout>
  );

  useEffect(() => {
    dispatch(
      slices.automation.actions.setAutomationGuidedAiDataForm({
        ...guidedAiForm,
        name:
          model.name || clause.name
            ? (model.name || clause.name).slice(0, 500)
            : guidedAiForm.name,
        description: model.description
          ? model.description
          : guidedAiForm.description,
      }),
    );
  }, [model, clause]);

  const [currentStepId, setCurrentStepId] = useState('basic-information');
  const [clauseName, setClauseName] = useState(
    guidedAiForm.name || model.name || clause.name,
  );
  const [descriptionValue, setDescriptionValue] = useState(
    guidedAiForm.description || model.description,
  );

  const [removeSamplesWarning, showRemoveSamplesWarning] = useModal({
    title: intl.formatMessage({
      id: 'GuidedAiForm.removeSamplesTitle',
      defaultMessage: 'Remove Samples?',
      description: 'Title for remove samples confirmation modal',
    }),
    content: intl.formatMessage({
      id: 'GuidedAiForm.removeSamplesContent',
      defaultMessage: 'Are you sure you want to remove the samples?',
      description: 'Content for remove samples confirmation modal',
    }),
    actionButton: {
      text: intl.formatMessage({
        id: 'GuidedAiForm.removeSamplesButton',
        defaultMessage: 'Remove',
        description: 'Button text for removing samples from the guided AI form',
      }),
      promise: async () => {
        setCurrentStepId('add-samples');
        dispatch(
          slices.automation.actions.setAutomationGuidedAiDataForm({
            ...guidedAiForm,
            uploadId: null,
            samples: [],
          }),
        );
      },
    },
    secondaryButton: {
      text: intl.formatMessage({
        id: 'GuidedAiForm.closeButton',
        defaultMessage: 'Close',
        description: 'Button text for closing the remove samples modal',
      }),
      variant: 'outlined',
      promise: async () => {},
    },
    disableCancelButton: true,
  });

  const hasExistingModel = guidedAiForm.errors?.some(
    (e) => e && e.includes('already exists'),
  );
  const hasCommonErrors = !hasExistingModel && guidedAiForm.errors?.length > 0;

  const doesClauseNameExist = !!clauseName;
  const isClauseNameOverLimit = clauseName.length > 500;
  const isClauseDescriptionOverLimit =
    (guidedAiForm.description?.length || 0) > 500;

  const isBasicInformationValid =
    doesClauseNameExist &&
    !isClauseNameOverLimit &&
    !isClauseDescriptionOverLimit;

  const modelNameFooter = `${clauseName.length} / 500`;
  const modelNameError = intl.formatMessage(
    {
      id: 'GuidedAiForm.modelNameError',
      defaultMessage:
        '{count} / 500 (Name cannot be longer than 500 characters)',
      description: 'Error message for clause name length over limit',
    },
    { count: clauseName.length },
  );
  const modelNameDupError = intl.formatMessage({
    id: 'GuidedAiForm.modelNameDupError',
    defaultMessage:
      'A Clause already exists with this name. Choose a different name.',
    description: 'Error message for duplicate clause name',
  });
  const modelDescriptionFooter = `${guidedAiForm.description.length} / 500`;
  const modelDescriptionError = intl.formatMessage(
    {
      id: 'GuidedAiForm.modelDescriptionError',
      defaultMessage:
        '{count} / 500 (Description cannot be longer than 500 characters)',
      description: 'Error message for clause description length over limit',
    },
    { count: guidedAiForm.description.length },
  );

  const clauseId = getPageSearchQueryByKey(QueryParamType.clauseId, model.id);

  const onDownloadSamples = () => {
    getSampleCsvFile({ clauseId, version: model.current?.version });
  };

  return (
    <Layout>
      <Box flex="none" w={SIDE_BAR_WIDTH} position="fixed">
        <Stepper
          currentStepId={currentStepId}
          name={intl.formatMessage({
            id: 'GuidedAiForm.stepperName',
            defaultMessage: 'guided ai steps',
            description: 'Stepper name for guided ai form',
          })}
          steps={[
            {
              id: 'basic-information',
              label: intl.formatMessage({
                id: 'GuidedAiForm.basicInformationTitle',
                defaultMessage: 'Basic Information',
                description: 'Stepper section title for basic information',
              }),
              isCompleted: isBasicInformationValid,
              error: isClauseNameOverLimit || isClauseDescriptionOverLimit,
            },
            {
              id: 'add-samples',
              label: intl.formatMessage({
                id: 'GuidedAiForm.addSamplesTitle',
                defaultMessage: 'Add Samples',
                description: 'Stepper section title for add samples',
              }),
              isCompleted: guidedAiForm.samples?.length,
              error: !!guidedAiForm.invalids,
            },
            {
              id: 'training-filters',
              label: intl.formatMessage({
                id: 'GuidedAiForm.trainingFiltersTitle',
                defaultMessage: 'Training Filters',
                description: 'Stepper section title for training filters',
              }),
              isCompleted: (filters?.[0]?.values?.length ?? 0) > 0,
            },
          ]}
        />
      </Box>
      <Box pl={SIDE_BAR_WIDTH}>
        {hasCommonErrors && (
          <Layout direction="column" mb={4} align="center">
            <Alert
              key="warning"
              enableIcon
              variant="danger"
              onDismiss={() =>
                dispatch(
                  slices.automation.actions.setAutomationGuidedAiDataForm({
                    ...guidedAiForm,
                    errors: [],
                  }),
                )
              }
            >
              <Layout direction="column" spacing={2}>
                {guidedAiForm.errors.map((e) => (
                  <Text variant="body">{e}</Text>
                ))}
              </Layout>
            </Alert>
          </Layout>
        )}
        <Layout preset="sections">
          <Section
            id="basic-information"
            title={intl.formatMessage({
              id: 'GuidedAiForm.basicInformationSectionTitle',
              defaultMessage: 'Basic Information',
              description: 'Section title for basic information',
            })}
          >
            <Layout preset="form-fields">
              <FormField
                name="custom-model-clause-name"
                label={intl.formatMessage({
                  id: 'GuidedAiForm.clauseNameLabel',
                  defaultMessage: 'Clause Name',
                  description: 'Label for clause name input',
                })}
                description={intl.formatMessage({
                  id: 'GuidedAiForm.clauseNameDescription',
                  defaultMessage:
                    "For what Clause are you creating this model? You'll use this Clause to find contracts after training is complete, so make sure this name is meaningful.",
                  description: 'Description for clause name input',
                })}
                input={TextInput}
                placeholder={intl.formatMessage({
                  id: 'GuidedAiForm.clauseNamePlaceholder',
                  defaultMessage: 'Create a New Clause',
                  description: 'Placeholder for clause name input',
                })}
                value={clauseName}
                disabled={!!clause.name}
                onChange={(name) => {
                  setCurrentStepId('basic-information');
                  setClauseName(name.trimLeft());
                  dispatch(
                    slices.automation.actions.setAutomationGuidedAiDataForm({
                      ...guidedAiForm,
                      name: name.trimLeft(),
                      errors: null,
                    }),
                  );
                }}
                footer={modelNameFooter}
                error={
                  (isClauseNameOverLimit && modelNameError) ||
                  (hasExistingModel && modelNameDupError)
                }
              ></FormField>
              <FormField
                name="custom-model-clause-description"
                label={intl.formatMessage({
                  id: 'GuidedAiForm.clauseDescriptionLabel',
                  defaultMessage: 'Description (Optional)',
                  description: 'Label for clause description input',
                })}
                description={intl.formatMessage({
                  id: 'GuidedAiForm.clauseDescriptionDescription',
                  defaultMessage:
                    'Give information that might be helpful for you and your colleagues in the future, such as why you created this new Clause.',
                  description: 'Description for clause description input',
                })}
                input={TextArea}
                placeholder={intl.formatMessage({
                  id: 'GuidedAiForm.clauseDescriptionPlaceholder',
                  defaultMessage: 'Description',
                  description: 'Placeholder for clause description input',
                })}
                value={descriptionValue}
                onChange={(description) => {
                  setDescriptionValue(description);
                  setCurrentStepId('basic-information');
                  dispatch(
                    slices.automation.actions.setAutomationGuidedAiDataForm({
                      ...guidedAiForm,
                      description,
                    }),
                  );
                }}
                footer={modelDescriptionFooter}
                error={
                  isClauseDescriptionOverLimit ? modelDescriptionError : null
                }
              ></FormField>
            </Layout>
          </Section>
          <Section
            id="add-samples"
            title={intl.formatMessage({
              id: 'GuidedAiForm.addSamplesSectionTitle',
              defaultMessage: 'Add Samples',
              description: 'Section title for add samples',
            })}
          >
            <Text>
              {intl.formatMessage({
                id: 'GuidedAiForm.samplesIntroText',
                defaultMessage:
                  "Samples give the model a place to start learning. You must provide a minimum of 10 samples that represent the Clause language you want to capture. Additionally, you could provide samples that represent language you'd like to avoid capturing.",
                description: 'Introductory text for samples section',
              })}
            </Text>
            <Text>
              <Link
                onClick={() =>
                  exportCsvBlob(CSV_TEMPLATE_HEADER, 'template.csv')
                }
              >
                {intl.formatMessage({
                  id: 'GuidedAiForm.samplesTemplateLink',
                  defaultMessage: 'Use this template',
                  description: 'Link text for CSV template',
                })}
              </Link>{' '}
              {intl.formatMessage({
                id: 'GuidedAiForm.samplesTemplateQuickStart',
                defaultMessage: 'to quickly get started.',
                description: 'Quick start text after template link',
              })}
            </Text>
            {model.stage === TrainingStage.Predicted && (
              <Layout direction="column">
                <Text>
                  {intl.formatMessage({
                    id: 'GuidedAiForm.retrainingIntro',
                    defaultMessage: 'For retraining:',
                    description: 'Intro text for retraining steps',
                  })}
                </Text>
                <Text>
                  {intl.formatMessage({
                    id: 'GuidedAiForm.retrainingStep1',
                    defaultMessage: 'Step 1: ',
                    description: 'Retraining step 1 prefix',
                  })}
                  <Link onClick={onDownloadSamples}>
                    {intl.formatMessage({
                      id: 'GuidedAiForm.retrainingStep1Link',
                      defaultMessage:
                        'Download the existing training data csv (# examples of the Assignment clause)',
                      description:
                        'Link text for downloading training data csv',
                    })}
                  </Link>
                </Text>
                <Text>
                  {intl.formatMessage({
                    id: 'GuidedAiForm.retrainingStep2',
                    defaultMessage:
                      'Step 2: Add/Edit/Remove examples form this csv',
                    description: 'Retraining step 2 text',
                  })}
                </Text>
                <Text>
                  {intl.formatMessage({
                    id: 'GuidedAiForm.retrainingStep3',
                    defaultMessage: 'Step 3: Save and re-upload',
                    description: 'Retraining step 3 text',
                  })}
                </Text>
              </Layout>
            )}
            {guidedAiForm.uploadId &&
            guidedAiForm.csvFile &&
            !guidedAiForm.invalids ? (
              <>
                <Layout
                  bg="background.quiet"
                  justify="space-between"
                  p={6}
                  spacing={3}
                  styles={{ border: 'border', borderRadius: 'm' }}
                >
                  <FileLabel file={guidedAiForm.csvFile} />
                  <IconButton
                    icon="x"
                    title={intl.formatMessage({
                      id: 'GuidedAiForm.removeFileButton',
                      defaultMessage: 'Remove file',
                      description: 'Title for remove file button',
                    })}
                    onClick={showRemoveSamplesWarning}
                  />
                </Layout>
              </>
            ) : (
              <FileInput
                file={guidedAiForm.invalids ? null : guidedAiForm.uploadId}
                layout="condensed"
                acceptedFiles={FileExtensionType.Csv}
                enableUpload={true}
                enableDropzone={true}
                enableMultiple={false}
                onChange={(f) => {
                  setCurrentStepId('add-samples');
                  uploadCsvFile(f[0])
                    .then((response) => {
                      dispatch(
                        slices.automation.actions.setAutomationGuidedAiCsvUploadId(
                          {
                            uploadId: response.id,
                            csvFile: f[0],
                          },
                        ),
                      );
                    })
                    .catch((error) => {
                      dispatch(
                        slices.automation.actions.setAutomationGuidedAiCsvUploadId(
                          {
                            csvFile: f[0],
                            invalids: error.response?.data,
                          },
                        ),
                      );
                    });
                }}
                error={guidedAiForm.invalids && trainingDataError}
              />
            )}
          </Section>
          <Section
            id="training-filters"
            title={intl.formatMessage({
              id: 'GuidedAiForm.trainingFiltersSectionTitle',
              defaultMessage: 'Train Model using Specific Contracts (Optional)',
              description: 'Section title for training filters',
            })}
          >
            <Layout direction="column" spacing={2}>
              <Text whiteSpace="pre-wrap">
                <CollapsibleText
                  limit={181}
                  text={TRAINING_FILTER_TEXT}
                ></CollapsibleText>
              </Text>
              {model.stage === TrainingStage.Predicted ? (
                <AutomationHubSearchingFilters retraining />
              ) : (
                <AutomationHubSearchingFilters training />
              )}
            </Layout>
          </Section>
        </Layout>
      </Box>
      {removeSamplesWarning}
    </Layout>
  );
};

export default GuidedAiForm;
